/**
 * Typed fetchers and React Query hooks for the Overview endpoints (BON-534).
 *
 * The shapes mirror `backend/src/services/overview-dashboard.types.ts` — see
 * `overview-types.ts`. The region-scoped calls address a region by its full
 * ancestor path, so `path` from any response row is what you pass back in.
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { apiFetch } from '../../../../lib/apiFetch';
import { useWorkshopBlocks } from '../../primitives/use-workshop-blocks';
import type { RegionalWorkshopEntry } from '../../regional/regionalApi';

import { pathParam } from './overview-path';
import type { AllRegionsOverviewDto, RegionOverviewDto } from './overview-types';

const STALE_30S = 30_000;

/**
 * How many levels of the hierarchy to ask for.
 *
 * We fetch the lot. There are tens of regions, the database caps nesting at
 * three levels, and rendering all of it costs nothing next to a second round
 * trip on every expand — so a row that offers to open always has its children
 * already in hand. The parameter stays on the API for when that stops being
 * true; this constant is the only thing that would need to change.
 */
export const OVERVIEW_DEPTH = 6;

async function fetchAllRegions(): Promise<AllRegionsOverviewDto> {
  const res = await apiFetch(`/api/v1/dashboards/overview?depth=${OVERVIEW_DEPTH}`);
  if (!res.ok) throw new Error(`Overview request failed (${res.status})`);
  return (await res.json()).data as AllRegionsOverviewDto;
}

async function fetchRegion(path: readonly string[]): Promise<RegionOverviewDto> {
  const res = await apiFetch(
    `/api/v1/dashboards/overview/${pathParam(path)}?depth=${OVERVIEW_DEPTH}`,
  );
  if (!res.ok) throw new Error(`Region overview request failed (${res.status})`);
  return (await res.json()).data as RegionOverviewDto;
}

async function fetchRegionWorkshops(
  path: readonly string[],
  fromDate: string,
  toDate: string,
): Promise<RegionalWorkshopEntry[]> {
  const query = new URLSearchParams({ from: fromDate, to: toDate });
  const res = await apiFetch(`/api/v1/dashboards/overview/${pathParam(path)}/workshops?${query}`);
  if (!res.ok) throw new Error(`Workshops request failed (${res.status})`);
  return (await res.json()).data as RegionalWorkshopEntry[];
}

/** The all-regions view: the day strip and the whole region roster. */
export function useAllRegionsOverview() {
  return useQuery({
    queryKey: ['dashboards.overview', 'all', OVERVIEW_DEPTH],
    queryFn: fetchAllRegions,
    staleTime: STALE_30S,
  });
}

/** One region at any depth, with its ancestors and its sub-regions. */
export function useRegionOverview(path: readonly string[]) {
  return useQuery({
    queryKey: ['dashboards.overview', 'region', path.join('/'), OVERVIEW_DEPTH],
    queryFn: () => fetchRegion(path),
    staleTime: STALE_30S,
    enabled: path.length > 0,
  });
}

/**
 * Workshops for a region and everything beneath it, over the calendar's
 * currently visible range. Disabled until the calendar reports a range, so the
 * first render does not fetch a window nobody is looking at.
 */
export function useRegionWorkshops(path: readonly string[], fromDate?: string, toDate?: string) {
  return useQuery({
    queryKey: ['dashboards.overview', 'workshops', path.join('/'), fromDate, toDate],
    queryFn: () => fetchRegionWorkshops(path, fromDate as string, toDate as string),
    // A new range (Month to List, say) keeps the last answer on screen rather
    // than an empty calendar that reads as "nothing on".
    placeholderData: keepPreviousData,
    staleTime: STALE_30S,
    enabled: Boolean(path.length > 0 && fromDate && toDate),
  });
}

/**
 * A region's workshops for the phone list: the next two weeks first, then a
 * block at a time as the viewer asks for more. Cached apart from the
 * calendar's lists, which hold plain arrays under their own keys.
 */
export function useRegionWorkshopsAhead(path: readonly string[]) {
  const region = path.join('/');
  return useWorkshopBlocks<RegionalWorkshopEntry>({
    keyParts: ['dashboards.overview', 'workshops-ahead', region],
    fetchBlock: ({ from, to }) => fetchRegionWorkshops(path, from, to),
    enabled: path.length > 0,
  });
}
